import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Button, Card, CircularProgress, SxProps, Typography } from '@mui/material';
import { useGetBlueprintPowerProfile } from 'features/blueprints';
import { PowerProfileChart } from 'greener-ui-library';
import { aggregateDataByMinute } from 'pages/Blueprints/NewBlueprint/utils/powerRequirementsUtils';
import { PowerProfileStatsDisplay } from 'pages/Blueprints/components/PowerProfileStatsDisplay';
import { convertPowerProfileJsonToDataPoints } from 'pages/Blueprints/utils/convertPowerProfileJsonToDataPoints';
import { getProjectDurationWeeks } from 'pages/Blueprints/utils/getProjectDurationWeeks';
import { Blueprint } from 'types';
import { currencyCodeToLocale } from 'utils/localization';

interface PowerRequirementsBoxProps {
  blueprintDetails?: Blueprint;
  readOnly?: boolean;
  sx?: SxProps;
  isAnimationActive?: boolean;
}

export const PowerRequirementsBox = ({
  blueprintDetails,
  readOnly = false,
  sx,
  isAnimationActive = true,
}: PowerRequirementsBoxProps) => {
  const { t } = useTranslation();

  const { powerProfileData: powerProfileDataFromApi, isLoading } = useGetBlueprintPowerProfile({
    blueprintId: String(blueprintDetails?.blueprintId || ''),
    enabled: !!blueprintDetails?.blueprintId && !readOnly,
  });

  // If readOnly (shared page), use powerProfile from blueprintDetails
  const powerProfileDataFromJson = useMemo(() => {
    if (readOnly && blueprintDetails?.powerProfile) {
      return convertPowerProfileJsonToDataPoints(blueprintDetails.powerProfile);
    }
    return null;
  }, [readOnly, blueprintDetails?.powerProfile]);

  const powerProfileData = powerProfileDataFromJson || powerProfileDataFromApi;
  const chartData1minInterval = useMemo(
    () => (powerProfileData ? aggregateDataByMinute(powerProfileData) : undefined),
    [powerProfileData]
  );

  const backendLoadProfile = blueprintDetails?.calculations?.[0]?.loadProfile;

  const projectDurationWeeks = getProjectDurationWeeks({
    startDate: blueprintDetails?.tsStart,
    endDate: blueprintDetails?.tsEnd,
  });

  if (!isLoading && (!chartData1minInterval || chartData1minInterval.length === 0)) {
    return (
      <Card sx={{ padding: 4, borderRadius: '8px', ...sx }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t('blueprints:wizard.steps.powerRequirements')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('blueprints:details.powerProfile.loadError')}
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ padding: 4, borderRadius: '8px', ...sx }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h3">{t('blueprints:wizard.steps.powerRequirements')}</Typography>

        {!readOnly && (
          <Button
            data-testid="edit-power-requirements-button"
            component={Link}
            to={`/solutions/${blueprintDetails?.blueprintId}/edit?step=2`}
          >
            {t('blueprints:details.proposedSolution.editButton')}
          </Button>
        )}
      </Box>

      <PowerProfileStatsDisplay
        locale={currencyCodeToLocale(blueprintDetails?.calculations?.[0]?.currencyCode)}
        peakPower={backendLoadProfile?.peakLoadKw}
        averagePower={backendLoadProfile?.avgLoadKw}
        energyRequiredWeekly={backendLoadProfile?.weeklyLoadKwh}
        projectDurationWeeks={projectDurationWeeks}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="220px">
          <CircularProgress />
        </Box>
      ) : chartData1minInterval && chartData1minInterval.length > 0 ? (
        <>
          <Box data-chart-id="power-profile" sx={{ borderRadius: 2, bgcolor: 'action.hover', py: 2, mb: 0.5 }}>
            <PowerProfileChart
              isAnimationActive={isAnimationActive}
              data={chartData1minInterval}
              height={220}
              peakPowerKw={backendLoadProfile?.peakLoadKw ?? undefined}
              averagePowerKw={backendLoadProfile?.avgLoadKw ?? undefined}
              title={t('blueprints:wizard.fields.powerProfile.title')}
              titleStyles={{ ml: 3 }}
              orientationWarningTitle={t('blueprints:wizard.fields.powerProfile.orientationWarningTitle')}
              orientationWarningMessage={t('blueprints:wizard.fields.powerProfile.orientationWarningMessage')}
            />
          </Box>
          <Typography variant="caption">{t('blueprints:details.powerProfile.disclaimer')}</Typography>
        </>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="220px">
          <Typography variant="body2" color="text.secondary">
            {t('blueprints:details.powerProfile.noData')}
          </Typography>
        </Box>
      )}
    </Card>
  );
};
